import { useAtomValue } from 'jotai'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { AnswerOptionSelector } from '~/components/features/AnswerOptionSelector'
import { useFormField } from '~/hooks/useFormField'
import { ensureExists, findByPath } from '~/utils'

import { linkAnswersInfoAtom } from '../../atoms'
import {
  type SurveyGroupSurvey,
  useMainQuerySurveys,
} from '../../hooks/useMainQuery'

import { type InputValues } from './utils'

export type AttachedOptionSelectorProps = {
  index: number
  survey: SurveyGroupSurvey
}

export const AttachedOptionSelector = ({
  index,
  survey,
}: AttachedOptionSelectorProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<InputValues>()
  const field = useFormField(
    control,
    `options.${index}.attachedOptionMap.${survey.id}`,
  )

  const linkAnswersInfo = useAtomValue(linkAnswersInfoAtom)
  const { question } = ensureExists(linkAnswersInfo, t('assertion_failed'))
  const surveys = useMainQuerySurveys()
  const answerOptions = React.useMemo(() => {
    const attachedQuestionId = question.attachedQuestionMap[survey.id]
    if (typeof attachedQuestionId !== 'string') {
      return []
    }

    const survey_ = findByPath(surveys, 'id', survey.id)
    const question_ = findByPath(
      survey_?.questions ?? [],
      'question_id',
      attachedQuestionId,
    )

    return question_?.answer_options ?? []
  }, [question.attachedQuestionMap, survey.id, surveys])

  return (
    <LabelingContainer label={survey.title} labelVariant="small">
      <AnswerOptionSelector
        answerOptions={answerOptions}
        disabled={answerOptions.length === 0}
        isNullSelectable={true}
        nullText={t('no_corresponding_options')}
        {...field}
      />
    </LabelingContainer>
  )
}
